import { Action, Dispatch } from "redux"
import {
  handleAuthorizedRequest,
  trackShareHolders,
  users,
  youtubeVideoShareHolders,
  releases,
  summaries,
  completeShareHolders,
  collaboratorInvitation,
  affiliate,
  tracks
} from "../apis"
import {
  getFilterString,
  getOrderByString,
  mapPaginatedDataToCamelCase,
  mapToCamelCase,
  mapToSnakeCase
} from "../utility"
import {
  AdminCollaboratorShareForm,
  AnalysisSummaries,
  AnalysisSummary,
  CollaboratorData,
  User,
  UserExpenseForm,
  UserForm,
  UserTrackShareData,
  UserTrackShareForm,
  UserYoutubeVideoShareData,
  UserYoutubeVideoShareForm
} from "../types"
import { toast } from "react-toastify"
import { errorFilledToastOptions, infoFilledToastOptions, successFilledToastOptions } from "../constants"
import { DateTime } from "luxon"

export const ADMIN_ACTION = {
  FETCH_ALL_USERS: "@ADMIN/FETCH_ALL_USERS",
  SELECT_USER: "@ADMIN/SELECT_USER",
  SAVE_USER: "@ADMIN/SAVE_USER",
  FETCH_USER_EXPENSE: "@ADMIN/FETCH_USER_EXPENSE",
  FETCH_AFFILIATE_LIST: "@ADMIN/FETCH_AFFILIATE_LIST",
  CREATE_USER_EXPENSE: "@ADMIN/CREATE_USER_EXPENSE",
  FETCH_TRACK_SHARE_DATA: "@ADMIN/FETCH_TRACK_SHARE_DATA",
  SELECT_TRACK_SHARE: "@ADMIN/SELECT_TRACK_SHARE",
  SELECT_PAYMENT_METHOD_USER: "@ADMIN/SELECT_PAYMENT_METHOD_USER",
  SAVE_TRACK_SHARE: "@ADMIN/SAVE_TRACK_SHARE",
  DELETE_TRACK_SHARE: "@ADMIN/DELETE_TRACK_SHARE",
  FETCH_YOUTUBE_VIDEO_SHARE_DATA: "@ADMIN/FETCH_YOUTUBE_VIDEO_SHARE_DATA",
  SELECT_YOUTUBE_VIDEO_SHARE: "@ADMIN/SELECT_YOUTUBE_VIDEO_SHARE",
  DELETE_YOUTUBE_VIDEO_SHARE: "@ADMIN/DELETE_YOUTUBE_VIDEO_SHARE",
  SAVE_YOUTUBE_VIDEO_SHARE: "@ADMIN/SAVE_YOUTUBE_VIDEO_SHARE",
  FETCH_LAST_UPDATE: "@ADMIN/FETCH_LAST_UPDATE",
  REFRESH_LABEL_CAMP: "@ADMIN/REFRESH_LABEL_CAMP",
  REFRESH_REPORTS: "@ADMIN/REFRESH_REPORTS",
  REFRESH_SUMMARIES: "@ADMIN/REFRESH_SUMMARIES",
  FETCH_SUMMARY: "@ADMIN/FETCH_SUMMARY",
  APPROVE_SUMMARY: "@ADMIN/APPROVE_SUMMARY",
  TRANSFER_BACK_SUMMARY: "@ADMIN/TRANSFER_BACK_SUMMARY",
  SELECT_SUMMARY_FILTER: "@ADMIN/SELECT_SUMMARY_FILTER",
  FETCH_COLLABORATOR_DATA: "@ADMIN/FETCH_COLLABORATOR_DATA",
  SELECT_COLLABORATOR: "@ADMIN/SELECT_COLLABORATOR",
  SEND_COLLABORATOR_INVITATION: "@ADMIN/SEND_COLLABORATOR_INVITATION",
  FETCH_MISSING_SHARE_HOLDER_COUNT: "@ADMIN/FETCH_MISSING_SHARE_HOLDER_COUNT",
  UPDATE_MISSING_SHARE_HOLDER_COUNT: "@ADMIN/UPDATE_MISSING_SHARE_HOLDER_COUNT",
  FETCH_ROYALTY_SUMMARY_DUPLICATE_COUNT: "@ADMIN/FETCH_ROYALTY_SUMMARY_DUPLICATE_COUNT",
  UPDATE_ROYALTY_SUMMARY_DUPLICATE_COUNT: "@ADMIN/UPDATE_ROYALTY_SUMMARY_DUPLICATE_COUNT",
  FETCH_RELEASE_EDIT_AGAIN_SUBMIT_COUNT: "@ADMIN/FETCH_RELEASE_EDIT_AGAIN_SUBMIT_COUNT",
  UPDATE_RELEASE_EDIT_AGAIN_SUBMIT_COUNT: "@ADMIN/UPDATE_RELEASE_EDIT_AGAIN_SUBMIT_COUNT",
  FETCH_RELEASE_EDIT_REQUEST_COUNT: "@ADMIN/FETCH_RELEASE_EDIT_REQUEST_COUNT",
  UPDATE_RELEASE_EDIT_REQUEST_COUNT: "@ADMIN/UPDATE_RELEASE_EDIT_REQUEST_COUNT",
  FETCH_USER_HAS_UPDATE_COUNT: "@ADMIN/FETCH_USER_HAS_UPDATE_COUNT",
  UPDATE_USER_HAS_UPDATE_COUNT: "@ADMIN/UPDATE_USER_HAS_UPDATE_COUNT",
  FETCH_USER_UNVERIFIED_CONTRACT_COUNT: "@ADMIN/FETCH_USER_UNVERIFIED_CONTRACT_COUNT",
  UPDATE_USER_UNVERIFIED_CONTRACT_COUNT: "@ADMIN/UPDATE_USER_UNVERIFIED_CONTRACT_COUNT",
  FETCH_USER_PAYMENT_METHOD_UPDATED_COUNT: "@ADMIN/FETCH_USER_PAYMENT_METHOD_UPDATED_COUNT",
  UPDATE_USER_PAYMENT_METHOD_UPDATED_COUNT: "@ADMIN/UPDATE_USER_PAYMENT_METHOD_UPDATED_COUNT"
}

export const fetchAllUsers =
  (
    limit: number,
    offset: number,
    filters: { [key: string]: string | number | boolean } | undefined,
    sortField?: string,
    sortDirection?: "asc" | "desc"
  ) =>
  async (dispatch: Dispatch<Action>) => {
    await handleAuthorizedRequest(
      async () => {
        const response = await users(
          `/?limit=${limit}&offset=${offset}${getFilterString(filters)}&admin=1${getOrderByString(
            sortField,
            sortDirection
          )}`,
          "get"
        )

        dispatch({
          type: ADMIN_ACTION.FETCH_ALL_USERS,
          payload: mapPaginatedDataToCamelCase<User>(response.data)
        })
      },
      "Error fetching users: ",
      dispatch
    )
  }

export const selectUser = (user: User | null) => async (dispatch: Dispatch<Action>) => {
  dispatch({
    type: ADMIN_ACTION.SELECT_USER,
    payload: user
  })
}

export const saveUser = (formValues: UserForm) => async (dispatch: Dispatch<Action>) => {
  await handleAuthorizedRequest(
    async () => {
      const permission_ids = formValues.permissionIds
      const email = formValues.username
      const requestPayload = {
        ...mapToSnakeCase(formValues),
        permission_ids,
        email
      }
      let sendEmailParam = ""
      if (formValues.sendEmail) {
        sendEmailParam = "?send_email=1"
      }
      const response = await users(`/create_user/${sendEmailParam}`, "post", requestPayload)
      if (response.data.error) {
        toast.error(`Error: ${response.data.error}`, errorFilledToastOptions)
        return
      }
      dispatch({
        type: ADMIN_ACTION.SAVE_USER,
        payload: mapToCamelCase(response.data),
        entityId: response.data.entity_id
      })

      toast.info("User saved. Refresh the page to see the changes.", successFilledToastOptions)
    },
    "Error saving user: ",
    dispatch
  )
}

export const fetchUserExpenseData = (userId: number) => async (dispatch: Dispatch<Action>) => {
  await handleAuthorizedRequest(
    async () => {
      const response = await users(`/fetch_user_expense/?user_id=${userId}`, "get")

      dispatch({
        type: ADMIN_ACTION.FETCH_USER_EXPENSE,
        payload: mapToCamelCase(response.data.model)
      })
    },
    "Error fetching user expense data: ",
    dispatch
  )
}

export const fetchAffiliateList = (userId: number) => async (dispatch: Dispatch<Action>) => {
  await handleAuthorizedRequest(
    async () => {
      const response = await affiliate(`/?admin=1&user_id=${userId}`, "get")

      dispatch({
        type: ADMIN_ACTION.FETCH_AFFILIATE_LIST,
        payload: mapToCamelCase(response.data)
      })
    },
    "Error fetching user affiliate data: ",
    dispatch
  )
}

export const saveUserExpenseData = (formValues: UserExpenseForm) => async (dispatch: Dispatch<Action>) => {
  await handleAuthorizedRequest(
    async () => {
      const requestPayload = {
        // @ts-ignore
        ...mapToSnakeCase(formValues)
      }
      const response = await users(`/create_user_expense/`, "post", requestPayload)
      dispatch({
        type: ADMIN_ACTION.CREATE_USER_EXPENSE,
        payload: mapToCamelCase(response.data.model)
      })
      toast.success("User Expense Saved.", successFilledToastOptions)
    },
    "Error saving user expense: ",
    dispatch
  )
}

export const applyUserExpenseToPreviousMonths =
  (userExpenseId: number, months: string) => async (dispatch: Dispatch<Action>) => {
    await handleAuthorizedRequest(
      async () => {
        const requestPayload = { id: userExpenseId, months }
        const response = await users(`/apply_user_expense_previous_months/`, "post", requestPayload)
        if (!response.data.success) {
          toast.error(`Error: ${response.data.message}`, { ...errorFilledToastOptions, autoClose: 100000 })
          return
        }
        toast.success("Applied to previous months successfully.", { ...successFilledToastOptions, autoClose: 100000 })
      },
      "Error applying user expense to past",
      dispatch
    )
  }

export const fetchUserTrackShareData =
  (
    id: number,
    limit: number,
    offset: number,
    filters: { [key: string]: string | number | boolean } | undefined,
    sortField?: string,
    sortDirection?: "asc" | "desc"
  ) =>
  async (dispatch: Dispatch<Action>) => {
    await handleAuthorizedRequest(
      async () => {
        const response = await trackShareHolders(
          `/?user=${id}&limit=${limit}&offset=${offset}${getFilterString(filters)}&${getOrderByString(
            sortField,
            sortDirection
          )}`,
          "get"
        )
        dispatch({
          type: ADMIN_ACTION.FETCH_TRACK_SHARE_DATA,
          payload: mapPaginatedDataToCamelCase(response.data)
        })
      },
      "Error fetching user share data: ",
      dispatch
    )
  }

export const selectTrackShareData = (data: UserTrackShareData | null) => async (dispatch: Dispatch<Action>) => {
  dispatch({
    type: ADMIN_ACTION.SELECT_TRACK_SHARE,
    payload: data
  })
}

export const paymentMethodsAdminEntry = (adminPanelUserId?: number) => async (dispatch: Dispatch<Action>) => {
  dispatch({
    type: ADMIN_ACTION.SELECT_PAYMENT_METHOD_USER,
    payload: adminPanelUserId
  })
}

export const saveTrackShareForm = (formValues: UserTrackShareForm) => async (dispatch: Dispatch<Action>) => {
  await handleAuthorizedRequest(
    async () => {
      const response = await trackShareHolders("/", "post", formValues)

      if (response.status === 203) {
        toast.error(response.data.message, errorFilledToastOptions)
        return
      }

      dispatch({
        type: ADMIN_ACTION.SAVE_TRACK_SHARE,
        payload: mapToCamelCase(response.data)
      })
      toast.success("Action successfully completed", successFilledToastOptions)
    },
    "Error saving track share form: ",
    dispatch
  )
}

export const deleteTrackShareData = (shareDataId: number) => async (dispatch: Dispatch<Action>) => {
  await handleAuthorizedRequest(
    async () => {
      const response = await trackShareHolders(`/${shareDataId}/`, "delete")

      if (response.status === 204) {
        dispatch({
          type: ADMIN_ACTION.DELETE_TRACK_SHARE,
          payload: shareDataId
        })
        toast.success("Track share deleted successfully.", successFilledToastOptions)
      }
    },
    "Deleting track share data has error.",
    dispatch
  )
}

export const fetchUserYoutubeVideoShareData =
  (
    id: number,
    limit: number,
    offset: number,
    filters: { [key: string]: string | number | boolean } | undefined,
    sortField?: string,
    sortDirection?: "asc" | "desc"
  ) =>
  async (dispatch: Dispatch<Action>) => {
    await handleAuthorizedRequest(
      async () => {
        const response = await youtubeVideoShareHolders(
          `/?user=${id}&limit=${limit}&offset=${offset}${getFilterString(filters)}&${getOrderByString(
            sortField,
            sortDirection
          )}`,
          "get"
        )

        dispatch({
          type: ADMIN_ACTION.FETCH_YOUTUBE_VIDEO_SHARE_DATA,
          payload: mapPaginatedDataToCamelCase(response.data)
        })
      },
      "Error fetching user share data: ",
      dispatch
    )
  }

export const selectYoutubeVideoShareData =
  (data: UserYoutubeVideoShareData | null) => async (dispatch: Dispatch<Action>) => {
    dispatch({
      type: ADMIN_ACTION.SELECT_YOUTUBE_VIDEO_SHARE,
      payload: data
    })
  }

export const deleteYoutubeVideoShareData = (shareDataId: number) => async (dispatch: Dispatch<Action>) => {
  await handleAuthorizedRequest(
    async () => {
      const response = await youtubeVideoShareHolders(`/${shareDataId}/`, "delete")

      if (response.status === 204) {
        dispatch({
          type: ADMIN_ACTION.DELETE_YOUTUBE_VIDEO_SHARE,
          payload: shareDataId
        })
        toast.success("Youtube video share deleted successfully.", successFilledToastOptions)
      }
    },
    "Deleting youtube share data has error: ",
    dispatch
  )
}

export const saveYoutubeVideoShareForm =
  (formValues: UserYoutubeVideoShareForm) => async (dispatch: Dispatch<Action>) => {
    await handleAuthorizedRequest(
      async () => {
        const response = await youtubeVideoShareHolders("/", "post", mapToSnakeCase(formValues))

        /*dispatch({
          type: ADMIN_ACTION.SAVE_YOUTUBE_VIDEO_SHARE,
          payload: mapToCamelCase(response.data)
        })*/
        toast.info("Action successfully completed. Refresh the page to see the changes.", infoFilledToastOptions)
      },
      "Error saving youtube video share form",
      dispatch
    )
  }

export const fetchLastUpdate = () => async (dispatch: Dispatch<Action>) => {
  await handleAuthorizedRequest(
    async () => {
      const response = await releases(`/labelcamp-request-status`, "get")
      response.data.results.sort((a, b) => a.id - b.id)
      const lastUpdate = response.data.results[response.data.results.length - 1]?.date
      const dateObject = DateTime.fromISO(lastUpdate)
      const formattedDate = dateObject.toFormat("dd LLLL yyyy, EEEE")
      dispatch({
        type: ADMIN_ACTION.FETCH_LAST_UPDATE,
        payload: formattedDate
      })
    },
    "Error fetching last update date.",
    dispatch
  )
}

export const refreshLabelCamp = () => async (dispatch: Dispatch<Action>) => {
  await handleAuthorizedRequest(
    async () => {
      const response = await releases(`/summary/refresh_assets/`, "post")
      const message = response.data
      dispatch({
        type: ADMIN_ACTION.REFRESH_LABEL_CAMP,
        payload: message
      })
      toast.success(message, successFilledToastOptions)
    },
    "Error on refreshing label camp.",
    dispatch
  )
}

export const refreshReports = (date: string) => async (dispatch: Dispatch<Action>) => {
  await handleAuthorizedRequest(
    async () => {
      const response = await releases(`/summary/refresh_reports/`, "post", { date })
      const message = response.data
      dispatch({
        type: ADMIN_ACTION.REFRESH_REPORTS,
        payload: message
      })
      toast.success(message, successFilledToastOptions)
    },
    "Error on refreshing reports.",
    dispatch
  )
}

export const refreshSummaries = (date: string, userId: number) => async (dispatch: Dispatch<Action>) => {
  await handleAuthorizedRequest(
    async () => {
      const response = await releases(`/summary/refresh_summaries/`, "post", { date, userId })
      const message = response.data
      dispatch({
        type: ADMIN_ACTION.REFRESH_SUMMARIES,
        payload: message
      })
      toast.success(message, successFilledToastOptions)
    },
    "Error on refreshing summaries.",
    dispatch
  )
}

export const fetchApprovalSummaries =
  (
    type: string,
    limit: number,
    offset: number,
    filters: { [key: string]: string | number | boolean } | undefined,
    sortField?: string,
    sortDirection?: "asc" | "desc"
  ) =>
  async (dispatch: Dispatch<Action>) => {
    await handleAuthorizedRequest(
      async () => {
        if (filters && "selected_date_option" in filters && filters["selected_date_option"]) {
          //@ts-ignore
          const dateList = filters["date_list"] as string[]
          const newDateList = dateList.map((yyyymm) => {
            const year = Number(yyyymm.slice(0, 4))
            const month = Number(yyyymm.slice(4, 6)) - 1 // JS months are 0-based

            const date = new Date(year, month)
            const monthsToAdd = Number(filters["selected_date_option"])
            date.setMonth(date.getMonth() - monthsToAdd)

            const newYear = date.getFullYear()
            const newMonth = String(date.getMonth() + 1).padStart(2, "0")

            return `${newYear}${newMonth}`
          })

          //@ts-ignore
          filters["date_list"] = newDateList
        }

        const response = await summaries(
          `/summary_table/?is_admin_page=1&limit=${limit}&offset=${offset}&type=${type}${getFilterString(
            filters
          )}&${getOrderByString(sortField, sortDirection)}`,
          "get"
        )

        if (filters && "selected_date_option" in filters && filters["selected_date_option"]) {
          //@ts-ignore
          const dateList = filters["date_list"] as string[]
          const newDateList = dateList.map((yyyymm) => {
            const year = Number(yyyymm.slice(0, 4))
            const month = Number(yyyymm.slice(4, 6)) - 1 // JS months are 0-based

            const date = new Date(year, month)
            const monthsToAdd = Number(filters["selected_date_option"])
            date.setMonth(date.getMonth() + monthsToAdd)

            const newYear = date.getFullYear()
            const newMonth = String(date.getMonth() + 1).padStart(2, "0")

            return `${newYear}${newMonth}`
          })

          //@ts-ignore
          filters["date_list"] = newDateList
        }

        dispatch({
          type: ADMIN_ACTION.FETCH_SUMMARY,
          payload: mapToCamelCase(response.data)
        })
      },
      "Error on fetching summaries.",
      dispatch
    )
  }

export const updateApprovalSummaries = (summariesUpdated: AnalysisSummaries) => async (dispatch: Dispatch<Action>) => {
  dispatch({
    type: ADMIN_ACTION.FETCH_SUMMARY,
    payload: mapToCamelCase({ ...summariesUpdated })
  })
}

export const approveSummary = (summary: AnalysisSummary, type: string) => async (dispatch: Dispatch<Action>) => {
  await handleAuthorizedRequest(
    async () => {
      const response = await summaries(`/approve_summary/?type=${type}`, "post", summary)
      const isPaymentApproved = response.data.paymentApproved
      dispatch({
        type: ADMIN_ACTION.APPROVE_SUMMARY,
        payload: { ...summary, approved: true, paymentApproved: isPaymentApproved, type }
      })

      toast.success("Report summary successfully saved. Refresh to see updated list.", successFilledToastOptions)
    },
    "Error on approving summary.",
    dispatch
  )
}

export const disapproveSummary = (summary: AnalysisSummary) => async (dispatch: Dispatch<Action>) => {
  await handleAuthorizedRequest(
    async () => {
      const response = await summaries(`/disapprove_summary/?id=${summary.id}`, "put")
      if (!response.data.success) {
        toast.error(
          "Error dissaproving summary. Check payment approved = False and Transfer date is empty",
          errorFilledToastOptions
        )
        return
      }
      dispatch({
        type: ADMIN_ACTION.APPROVE_SUMMARY,
        payload: { ...summary, approved: false }
      })

      toast.success("Report summary successfully disapproved. Refresh to see updated list.", successFilledToastOptions)
    },
    "Error on disapproving summary.",
    dispatch
  )
}

export const transferSummary =
  (summary: AnalysisSummary, transferDate: string) => async (dispatch: Dispatch<Action>) => {
    await handleAuthorizedRequest(
      async () => {
        const response = await summaries(`/transfer_summary/`, "post", {
          id: summary.id,
          transfer_date: transferDate
        })
        const isSuccess = response.data.success

        if (!isSuccess) {
          toast.error("Error transfering summary.", errorFilledToastOptions)
          return
        }
        dispatch({
          type: ADMIN_ACTION.APPROVE_SUMMARY,
          payload: {
            ...summary,
            approved: true,
            paymentApproved: true,
            totalNet: 0,
            totalGross: 0,
            totalAmount: 0,
            totalNetAll: 0,
            transferDate: "√"
          }
        })

        toast.success("Report summary successfully transfered. Refresh to see updated list.", successFilledToastOptions)
      },
      "Error on transfering summary.",
      dispatch
    )
  }

export const transferBackSummary = (summary: AnalysisSummary) => async (dispatch: Dispatch<Action>) => {
  await handleAuthorizedRequest(
    async () => {
      const response = await summaries(`/transfer_back_summary/?id=${summary.id}`, "put")
      if (!response.data.success) {
        toast.error(response.data.message, errorFilledToastOptions)
        return
      }
      dispatch({
        type: ADMIN_ACTION.TRANSFER_BACK_SUMMARY,
        payload: { ...summary, approved: false }
      })

      toast.success(`Report transfered back to ${summary.transferDate} successfully`, successFilledToastOptions)
    },
    "Error on disapproving summary.",
    dispatch
  )
}

export const fetchTrackCollaboratorData = (trackId: number) => async (dispatch: Dispatch<Action>) => {
  await handleAuthorizedRequest(
    async () => {
      const response = await completeShareHolders(`?track_id=${trackId}`, "get")
      dispatch({
        type: ADMIN_ACTION.FETCH_COLLABORATOR_DATA,
        payload: response.data
      })
    },
    "Error fetching track share data: ",
    dispatch
  )
}

export const selectCollaboratorData = (data: CollaboratorData | null) => async (dispatch: Dispatch<Action>) => {
  dispatch({
    type: ADMIN_ACTION.SELECT_COLLABORATOR,
    payload: data
  })
}

export const saveCollaboratorForm = (formValues: AdminCollaboratorShareForm) => async (dispatch: Dispatch<Action>) => {
  const body = {
    ...formValues,
    roles: formValues.roles.toString()
  }
  await handleAuthorizedRequest(
    async () => {
      const response = await collaboratorInvitation("/", "post", body)

      if (response.status === 203) {
        toast.error(response.data.message, errorFilledToastOptions)
        return
      }

      dispatch({
        type: ADMIN_ACTION.SEND_COLLABORATOR_INVITATION,
        payload: response.data
      })
      toast.success("Collaborator invitation sent.", successFilledToastOptions)
    },
    "Error sending invitation to collaborator.",
    dispatch
  )
}

export const fetchMissingTrackShareHolderCount = () => async (dispatch: Dispatch<Action>) => {
  await handleAuthorizedRequest(
    async () => {
      const response = await tracks(`/missing_share_holder_count/`, "get")

      const count = response.data.count
      dispatch({
        type: ADMIN_ACTION.FETCH_MISSING_SHARE_HOLDER_COUNT,
        payload: count
      })
    },
    "Error on fetching missing track share holder count.",
    dispatch
  )
}

export const updateMissingTrackShareHolderCount = (count: number) => async (dispatch: Dispatch<Action>) => {
  await handleAuthorizedRequest(
    async () => {
      dispatch({
        type: ADMIN_ACTION.UPDATE_MISSING_SHARE_HOLDER_COUNT,
        payload: count
      })
    },
    "Error on updating missing track share holder count.",
    dispatch
  )
}

export const fetchRoyaltySummaryDuplicateCount = () => async (dispatch: Dispatch<Action>) => {
  await handleAuthorizedRequest(
    async () => {
      const response = await summaries(`/summary_duplicate_count/`, "get")

      const count = response.data.count
      dispatch({
        type: ADMIN_ACTION.FETCH_ROYALTY_SUMMARY_DUPLICATE_COUNT,
        payload: count
      })
    },
    "Error on fetching royalty summary duplicate count.",
    dispatch
  )
}

export const updateRoyaltySummaryDuplicateCount = (count: number) => async (dispatch: Dispatch<Action>) => {
  await handleAuthorizedRequest(
    async () => {
      dispatch({
        type: ADMIN_ACTION.UPDATE_ROYALTY_SUMMARY_DUPLICATE_COUNT,
        payload: count
      })
    },
    "Error on updating royalty summary duplicate count.",
    dispatch
  )
}

export const fetchReleaseEditAgainSubmitCount = () => async (dispatch: Dispatch<Action>) => {
  await handleAuthorizedRequest(
    async () => {
      const response = await releases(`/release/release_edit_again_submit_count/`, "get")

      const count = response.data.count
      dispatch({
        type: ADMIN_ACTION.FETCH_RELEASE_EDIT_AGAIN_SUBMIT_COUNT,
        payload: count
      })
    },
    "Error on fetching release edit again submit count.",
    dispatch
  )
}

export const updateReleaseEditAgainSubmitCount = (count: number) => async (dispatch: Dispatch<Action>) => {
  await handleAuthorizedRequest(
    async () => {
      dispatch({
        type: ADMIN_ACTION.UPDATE_RELEASE_EDIT_AGAIN_SUBMIT_COUNT,
        payload: count
      })
    },
    "Error on updating release edit again submit count.",
    dispatch
  )
}

export const fetchReleaseEditRequestCount = () => async (dispatch: Dispatch<Action>) => {
  await handleAuthorizedRequest(
    async () => {
      const response = await releases(`/release/?artist_edit_request=true&limit=1`, "get")

      const count = response.data.count
      dispatch({
        type: ADMIN_ACTION.FETCH_RELEASE_EDIT_REQUEST_COUNT,
        payload: count
      })
    },
    "Error on fetching release edit request count.",
    dispatch
  )
}

export const updateReleaseEditRequestCount = (count: number) => async (dispatch: Dispatch<Action>) => {
  await handleAuthorizedRequest(
    async () => {
      dispatch({
        type: ADMIN_ACTION.UPDATE_RELEASE_EDIT_REQUEST_COUNT,
        payload: count
      })
    },
    "Error on updating release edit request count.",
    dispatch
  )
}

export const fetchUserHasUpdateCount = () => async (dispatch: Dispatch<Action>) => {
  await handleAuthorizedRequest(
    async () => {
      const response = await users(`/has_update_count/`, "get")

      const count = response.data.count
      dispatch({
        type: ADMIN_ACTION.FETCH_USER_HAS_UPDATE_COUNT,
        payload: count
      })
    },
    "Error on fetching user has update count.",
    dispatch
  )
}

export const updateUserHasUpdateCount = (count: number) => async (dispatch: Dispatch<Action>) => {
  await handleAuthorizedRequest(
    async () => {
      dispatch({
        type: ADMIN_ACTION.UPDATE_USER_HAS_UPDATE_COUNT,
        payload: count
      })
    },
    "Error on updating user has update count.",
    dispatch
  )
}

export const fetchUserPaymentMethodUpdatedCount = () => async (dispatch: Dispatch<Action>) => {
  await handleAuthorizedRequest(
    async () => {
      const response = await users(`/payment_method_updated_count/`, "get")

      const count = response.data.count
      dispatch({
        type: ADMIN_ACTION.FETCH_USER_PAYMENT_METHOD_UPDATED_COUNT,
        payload: count
      })
    },
    "Error on fetching users count with updated payment methods.",
    dispatch
  )
}

export const updateUserPaymentMethodUpdatedCount = (count: number) => async (dispatch: Dispatch<Action>) => {
  await handleAuthorizedRequest(
    async () => {
      dispatch({
        type: ADMIN_ACTION.UPDATE_USER_PAYMENT_METHOD_UPDATED_COUNT,
        payload: count
      })
    },
    "Error on updating users count with updated payment methods.",
    dispatch
  )
}

export const fetchUserUnverifiedContractCount = () => async (dispatch: Dispatch<Action>) => {
  await handleAuthorizedRequest(
    async () => {
      const response = await users(`/unverified_contract_count/`, "get")

      const count = response.data.count
      dispatch({
        type: ADMIN_ACTION.FETCH_USER_UNVERIFIED_CONTRACT_COUNT,
        payload: count
      })
    },
    "Error on fetching users count with unverified contract.",
    dispatch
  )
}

export const updateUserUnverifiedContractCount = (count: number) => async (dispatch: Dispatch<Action>) => {
  await handleAuthorizedRequest(
    async () => {
      dispatch({
        type: ADMIN_ACTION.UPDATE_USER_UNVERIFIED_CONTRACT_COUNT,
        payload: count
      })
    },
    "Error on updating user unverified contract count.",
    dispatch
  )
}